import { ModuleWMSAction } from '@kanzi-apes/kanzi-models';
import { MenuItem } from 'primeng/api';

/**
 * Determines whether a menu item is allowed based on the user's permission list.
 * An item is allowed when its permission is present in the list, or when it has
 * no permission requirement (undefined).
 * @param permission - The permission (automationId) required by the menu item, or undefined when the item has no permission requirement
 * @param permissionList - The list of permissions granted to the current user
 * @returns True when the item is allowed for the user, false otherwise
 */
function validator(
  permission: string | undefined,
  permissionList: string[]
): boolean {
  return (
    permissionList.filter((item) => item === permission).length > 0 ||
    permission === undefined
  );
}

/**
 * Recursively validates a WMS menu tree against the user's permissions and
 * computes the visibility of each item.
 *
 * An item that is explicitly configured with `visible: false` is always kept
 * hidden regardless of permissions. This lets the static menu config opt items
 * out of the UI (e.g. "Crear Nuevo" for Terceros) without them being re-enabled
 * by the permission check, which only looks at `automationId`.
 * @param menuItems - The menu items to validate
 * @param permissionList - The list of permissions granted to the current user
 * @returns A new array of menu items with computed `visible` flags
 */
export function menuWMSValidator(
  menuItems: MenuItem[],
  permissionList: string[]
): MenuItem[] {
  return menuItems.map((menuItem) => {
    return {
      ...menuItem,
      visible:
        menuItem.visible === false
          ? false
          : validator(menuItem.automationId, permissionList),
      items: menuItem.items
        ? menuItem.items.length > 0
          ? menuWMSValidator(menuItem.items, permissionList)
          : undefined
        : undefined,
    };
  });
}
